import React, { useEffect, useState } from 'react';

import api from '../utils/api';
import Loader from '../lib/components/Loader';
import './Standings.css';

const record = ({ wins, losses, ties }) => (ties > 0 ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`);

const Movement = ({ movement }) => {
    if (movement === null || movement === undefined) {
        return null;
    }
    if (movement === 0) {
        return <span className="text-muted ms-1">-</span>;
    }
    return movement > 0
        ? <span className="text-success ms-1">▲{movement}</span>
        : <span className="text-danger ms-1">▼{Math.abs(movement)}</span>;
};

export const StandingsTable = ({ standings, tableClassName = '' }) => (
    <div className="table-responsive">
        <table className={`table table-sm table-dark table-striped standings-table align-middle ${tableClassName}`}>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Team</th>
                    <th className="text-end">Record</th>
                    <th className="text-end">PF</th>
                </tr>
            </thead>
            <tbody>
                {standings.map(row => (
                    <tr key={row.manager}>
                        <td>{row.rank}</td>
                        <td>{row.manager}</td>
                        <td className="text-end">{record(row)}</td>
                        <td className="text-end">{row.pointsFor.toFixed(2)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const PowerRankingsTable = ({ powerRankings }) => (
    <div>
        <div className="table-responsive">
            <table className="table table-sm table-dark table-striped standings-table align-middle">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Team</th>
                        <th className="text-end">Score</th>
                        <th className="text-end">Pts</th>
                        <th className="text-end">Hi/Lo</th>
                        <th className="text-end">Record</th>
                    </tr>
                </thead>
                <tbody>
                    {powerRankings.map(row => (
                        <tr key={row.manager}>
                            <td>{row.rank}</td>
                            <td>
                                {row.manager}
                                <Movement movement={row.movement} />
                            </td>
                            <td className="text-end fw-bold">{row.breakdown.total.toFixed(1)}</td>
                            <td className="text-end">{row.breakdown.points.toFixed(1)}</td>
                            <td className="text-end">{row.breakdown.highLow.toFixed(1)}</td>
                            <td className="text-end">{row.breakdown.record.toFixed(1)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <p className="text-light opacity-75 small">
            Score = Pts + Hi/Lo + Record, where 50 is an average team. Pts is from average points per week,
            Hi/Lo from best + worst week, and Record from win%.
        </p>
    </div>
);

/** The Standings / Power Rankings tabs and their tables; shared by the page and the home card. */
export const StandingsTabs = ({ data }) => {
    const [tab, updateTab] = useState('standings');
    return (
        <div>
            <ul className="nav nav-tabs standings-tabs mb-3">
                <li className="nav-item">
                    <button
                        className={`nav-link ${tab === 'standings' ? 'active' : ''}`}
                        onClick={() => updateTab('standings')}
                    >
                        Standings
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${tab === 'power' ? 'active' : ''}`}
                        onClick={() => updateTab('power')}
                    >
                        Power Rankings
                    </button>
                </li>
            </ul>
            {tab === 'standings'
                ? <StandingsTable standings={data.standings} />
                : <PowerRankingsTable powerRankings={data.powerRankings} />}
        </div>
    );
};

const Standings = () => {
    const [data, updateData] = useState(null);
    const [loading, updateLoading] = useState(true);
    const [isError, updateIsError] = useState(false);

    useEffect(() => {
        api.get('/api/v1/standings')
            .then(({ data }) => {
                updateData(data);
                updateIsError(false);
                updateLoading(false);
            })
            .catch(() => {
                updateIsError(true);
                updateLoading(false);
            });
    }, []);

    const content = () => {
        if (loading) {
            return <Loader />;
        }
        if (isError) {
            return <div className="alert alert-danger">Error loading standings!</div>;
        }
        if (data.week === null) {
            return <div className="alert alert-info">Standings will show up after Week 1.</div>;
        }
        return <StandingsTabs data={data} />;
    };

    return (
        <div className="standings-page">
            <h2>Standings</h2>
            {data && data.week !== null && (
                <p className="text-light small">After Week {data.week}. Updated Tuesdays.</p>
            )}
            {content()}
        </div>
    );
};

export default Standings;
